import { z } from "zod";
import type { Action, AvailabilityConfig } from "../shared/types";

/** Rules of one booking action; limits mirror backend `app/core/constants/bookings.py`. */
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
/** "Copy to all weekdays" writes Monday to Friday. */
export const WEEKDAY_TARGETS: readonly Weekday[] = ["mon", "tue", "wed", "thu", "fri"];

export const LIMITS = {
  windowsPerDay: 4,
  blockedDates: 120,
  location: 200,
  zone: 64,
  slotMinutes: { min: 10, max: 240, fallback: 30 },
  bufferMinutes: { min: 0, max: 120, fallback: 0 },
  minNoticeHours: { min: 0, max: 720, fallback: 12 },
  horizonDays: { min: 1, max: 180, fallback: 30 },
} as const;

export const DEFAULT_OPEN = "09:00";
export const DEFAULT_CLOSE = "17:00";
const LAST_CLOCK = "23:59";
const SUGGESTED_RANGE_MINUTES = 60;
const MINUTES_PER_HOUR = 60;
const CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type AvailabilityMode = "auto" | "request";
/** `GET /v1/agents/:id/actions` → `booking_mode`: how visitors are served right now. */
export type BookingMode = "calendar" | "request" | "unconfigured";

export interface HoursRange {
  open: string;
  close: string;
}
export type WeeklyHours = Record<Weekday, HoursRange[]>;

/** Editor form shape (arrays of objects so `useFieldArray` can drive them). */
export interface AvailabilityValues {
  mode: AvailabilityMode;
  /** Empty = the workspace time zone. */
  timezone: string;
  weekly_hours: WeeklyHours;
  slot_minutes: number;
  buffer_minutes: number;
  min_notice_hours: number;
  horizon_days: number;
  blocked_dates: { date: string }[];
  location: string;
  create_meet: boolean;
}

export function isBookingAction(action: Pick<Action, "kind">): boolean {
  return action.kind === "booking";
}

export function bookingModeOf(action: Pick<Action, "kind" | "booking_mode">): BookingMode | null {
  if (!isBookingAction(action)) return null;
  if (action.booking_mode === "calendar" || action.booking_mode === "request") return action.booking_mode;
  return "unconfigured";
}

export function toMinutes(clock: string): number {
  const [hours = "0", minutes = "0"] = clock.split(":");
  return Number(hours) * MINUTES_PER_HOUR + Number(minutes);
}

function toClock(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function isIsoDate(value: string): boolean {
  const match = DATE_PATTERN.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}

export function isKnownZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function emptyWeek(): WeeklyHours {
  return { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
}

export function defaultAvailability(): AvailabilityValues {
  const week = emptyWeek();
  for (const day of WEEKDAY_TARGETS) week[day] = [{ open: DEFAULT_OPEN, close: DEFAULT_CLOSE }];
  return {
    mode: "auto",
    timezone: "",
    weekly_hours: week,
    slot_minutes: LIMITS.slotMinutes.fallback,
    buffer_minutes: LIMITS.bufferMinutes.fallback,
    min_notice_hours: LIMITS.minNoticeHours.fallback,
    horizon_days: LIMITS.horizonDays.fallback,
    blocked_dates: [],
    location: "",
    create_meet: false,
  };
}

const storedSchema = z.object({
  mode: z.enum(["auto", "request"]).catch("auto"),
  timezone: z.string().nullable().catch(null),
  weekly_hours: z.record(z.array(z.object({ open: z.string(), close: z.string() }))).catch({}),
  slot_minutes: z.number().catch(LIMITS.slotMinutes.fallback),
  buffer_minutes: z.number().catch(LIMITS.bufferMinutes.fallback),
  min_notice_hours: z.number().catch(LIMITS.minNoticeHours.fallback),
  horizon_days: z.number().catch(LIMITS.horizonDays.fallback),
  blocked_dates: z.array(z.string()).catch([]),
  location: z.string().nullable().catch(null),
  create_meet: z.boolean().catch(false),
});

/** Form values for the stored rules (`ActionOut.availability`); defaults while none are saved. */
export function availabilityFromAction(action: Pick<Action, "availability">): AvailabilityValues {
  if (!action.availability) return defaultAvailability();
  const stored = storedSchema.parse(action.availability);
  const week = emptyWeek();
  for (const day of WEEKDAYS) week[day] = (stored.weekly_hours[day] ?? []).map(({ open, close }) => ({ open, close }));
  return {
    mode: stored.mode,
    timezone: stored.timezone ?? "",
    weekly_hours: week,
    slot_minutes: stored.slot_minutes,
    buffer_minutes: stored.buffer_minutes,
    min_notice_hours: stored.min_notice_hours,
    horizon_days: stored.horizon_days,
    blocked_dates: stored.blocked_dates.map((date) => ({ date })),
    location: stored.location ?? "",
    create_meet: stored.create_meet,
  };
}

function sortedRanges(ranges: readonly HoursRange[]): HoursRange[] {
  return ranges.map(({ open, close }) => ({ open, close })).sort((a, b) => toMinutes(a.open) - toMinutes(b.open));
}

/** `PATCH /v1/actions/{id}` body: closed days omitted, ranges and dates sorted, blanks → `null`. */
export function toAvailabilityConfig(values: AvailabilityValues): AvailabilityConfig {
  const weekly_hours: Record<string, HoursRange[]> = {};
  for (const day of WEEKDAYS) {
    const ranges = sortedRanges(values.weekly_hours[day]);
    if (ranges.length > 0) weekly_hours[day] = ranges;
  }
  return {
    mode: values.mode,
    timezone: values.timezone.trim() || null,
    weekly_hours,
    slot_minutes: values.slot_minutes,
    buffer_minutes: values.buffer_minutes,
    min_notice_hours: values.min_notice_hours,
    horizon_days: values.horizon_days,
    blocked_dates: [...new Set(values.blocked_dates.map((entry) => entry.date))].sort(),
    location: values.location.trim() || null,
    create_meet: values.create_meet,
  };
}

/** The next range to offer on "Add time range": the hour after the last one, else office hours. */
export function suggestRange(ranges: readonly HoursRange[]): HoursRange {
  const last = ranges.at(-1);
  if (!last) return { open: DEFAULT_OPEN, close: DEFAULT_CLOSE };
  const open = toMinutes(last.close);
  const close = Math.min(open + SUGGESTED_RANGE_MINUTES, toMinutes(LAST_CLOCK));
  if (close <= open) return { open: DEFAULT_OPEN, close: DEFAULT_CLOSE };
  return { open: toClock(open), close: toClock(close) };
}

/** Copies one day's ranges onto Monday to Friday; other days stay as they are. */
export function copyToWeekdays(week: WeeklyHours, from: Weekday): WeeklyHours {
  const next = { ...week };
  for (const day of WEEKDAY_TARGETS) next[day] = week[from].map(({ open, close }) => ({ open, close }));
  return next;
}

export type BlockedDateResult = { ok: true; dates: string[] } | { ok: false; reason: "invalid" | "duplicate" | "full" };

/** Adds one date to the blocked list (sorted, unique, at most 120). */
export function addBlockedDate(dates: readonly string[], date: string): BlockedDateResult {
  if (!isIsoDate(date)) return { ok: false, reason: "invalid" };
  if (dates.includes(date)) return { ok: false, reason: "duplicate" };
  if (dates.length >= LIMITS.blockedDates) return { ok: false, reason: "full" };
  return { ok: true, dates: [...dates, date].sort() };
}

/** Localized error copy, injected so the same schema serves the UI (`t`) and tests. */
export interface AvailabilityMessages {
  clock: string;
  rangeOrder: string;
  overlap: string;
  maxRanges: string;
  noOpenDay: string;
  slotMinutes: string;
  bufferMinutes: string;
  minNoticeHours: string;
  horizonDays: string;
  dateInvalid: string;
  blockedMax: string;
  locationMax: string;
  timezone: string;
}

function whole(message: string, bounds: { min: number; max: number }) {
  return z.number({ invalid_type_error: message, required_error: message }).int(message).min(bounds.min, message).max(bounds.max, message);
}

function rangeSchema(messages: AvailabilityMessages) {
  const clock = z.string().regex(CLOCK_PATTERN, messages.clock);
  return z.object({ open: clock, close: clock }).superRefine((range, ctx) => {
    const valid = CLOCK_PATTERN.test(range.open) && CLOCK_PATTERN.test(range.close);
    if (valid && toMinutes(range.open) >= toMinutes(range.close)) ctx.addIssue({ code: "custom", path: ["close"], message: messages.rangeOrder });
  });
}

function overlaps(ranges: readonly HoursRange[]): boolean {
  const ordered = sortedRanges(ranges.filter((r) => CLOCK_PATTERN.test(r.open) && CLOCK_PATTERN.test(r.close)));
  return ordered.some((later, index) => index > 0 && toMinutes(later.open) < toMinutes(ordered[index - 1]?.close ?? "00:00"));
}

/** Same rules as the backend `AvailabilityConfig`; day-level issues land on `weekly_hours.<day>.root`. */
export function availabilitySchema(messages: AvailabilityMessages) {
  const day = z.array(rangeSchema(messages));
  return z
    .object({
      mode: z.enum(["auto", "request"]),
      timezone: z
        .string()
        .trim()
        .max(LIMITS.zone, messages.timezone)
        .refine((zone) => zone === "" || isKnownZone(zone), messages.timezone),
      weekly_hours: z.object({ mon: day, tue: day, wed: day, thu: day, fri: day, sat: day, sun: day }),
      slot_minutes: whole(messages.slotMinutes, LIMITS.slotMinutes),
      buffer_minutes: whole(messages.bufferMinutes, LIMITS.bufferMinutes),
      min_notice_hours: whole(messages.minNoticeHours, LIMITS.minNoticeHours),
      horizon_days: whole(messages.horizonDays, LIMITS.horizonDays),
      blocked_dates: z
        .array(z.object({ date: z.string().refine(isIsoDate, messages.dateInvalid) }))
        .max(LIMITS.blockedDates, messages.blockedMax),
      location: z.string().trim().max(LIMITS.location, messages.locationMax),
      create_meet: z.boolean(),
    })
    .superRefine((values, ctx) => {
      for (const key of WEEKDAYS) {
        const ranges = values.weekly_hours[key];
        if (ranges.length > LIMITS.windowsPerDay) ctx.addIssue({ code: "custom", path: ["weekly_hours", key, "root"], message: messages.maxRanges });
        else if (overlaps(ranges)) ctx.addIssue({ code: "custom", path: ["weekly_hours", key, "root"], message: messages.overlap });
      }
      if (WEEKDAYS.every((key) => values.weekly_hours[key].length === 0)) ctx.addIssue({ code: "custom", path: ["weekly_hours", "root"], message: messages.noOpenDay });
    });
}
